import {
  ExpensableGroupsDataTableDocument,
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  AddGroupToPayrollDocument,
  ApproveGroupDocument,
  CreateGroupDocument,
  DeleteGroupDocument,
  ExpensablesDataCollectionDocument,
  ExpensablesPendingApprovalWidgetDocument,
  ExpensesApprovedWidgetDocument,
  ExportGroupPdfDocument,
  GetGroupsDocument,
  GroupDetailFragment,
  MyGroupedExpensablesDataCollectionDocument,
  PayGroupWithSepaDocument,
  PayrollPolicyPeriodTypesPeriodTypeEnum,
  ReimbursementsApprovedWidgetDocument,
  ReimbursementsPendingApprovalWidgetDocument,
  RejectGroupDocument,
  RemoveGroupFromPayrollDocument,
  RestartGroupReviewDocument,
  RevertPaidGroupDocument,
  SendGroupForApprovalDocument,
  SetGroupToPaidDocument,
  UpdateGroupDocument,
  UpdateGroupMutationVariables,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'

export type Group = GroupDetailFragment

export interface UseGroup {
  id: number
  employeeId?: number
  includeAttachments?: boolean
  includeDrafts?: boolean
  skip?: boolean
}

export const useCreate = () => {
  const [createExpensableGroup, { loading }] = useMutation(CreateGroupDocument, {
    extractorFn: (data) => data.expensesMutations.createExpensesExpensableGroup,
    refetchQueries: [ExpensableGroupsDataTableDocument],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    loading,
    create: (name: string, companyId: number) =>
      createExpensableGroup({ variables: { name, companyId } }),
  }
}

export const useUpdate = () => {
  const [updateExpensableGroup, { loading }] = useMutation(UpdateGroupDocument, {
    extractorFn: (data) => data.expensesMutations.updateExpensesExpensableGroup,
  })

  return {
    loading,
    update: (variables: UpdateGroupMutationVariables) => updateExpensableGroup({ variables }),
  }
}

export const useSendForApproval = () => {
  const [sendGroupForApproval, { loading }] = useMutation(SendGroupForApprovalDocument, {
    extractorFn: (data) => data.expensesMutations.readyToReviewExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    sendForApproval: (id: number, companyId: number) =>
      sendGroupForApproval({ variables: { id, companyId } }),
    loading,
  }
}

export const useApprove = () => {
  const [approveGroup, { loading }] = useMutation(ApproveGroupDocument, {
    extractorFn: (data) => data.expensesMutations.approveExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
      ExpensablesPendingApprovalWidgetDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    approve: (id: number, companyId: number, overrideApprovalFlow?: boolean) =>
      approveGroup({ variables: { id, overrideApprovalFlow, companyId } }),
    loading,
  }
}

export const useSetToPaid = () => {
  const [setToPaidGroup, { loading }] = useMutation(SetGroupToPaidDocument, {
    extractorFn: (data) => data.expensesMutations.setToPaidExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    setToPaid: (id: number, companyId: number) => setToPaidGroup({ variables: { id, companyId } }),
    loading,
  }
}

export const useRevertPaid = () => {
  const [revertPaidGroup, { loading }] = useMutation(RevertPaidGroupDocument, {
    extractorFn: (data) => data.expensesMutations.revertPaidExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    revertPaid: (id: number, companyId: number) =>
      revertPaidGroup({ variables: { id, companyId } }),
    loading,
  }
}

export const useReject = () => {
  const [rejectGroup, { loading }] = useMutation(RejectGroupDocument, {
    extractorFn: (data) => data.expensesMutations.rejectExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
      ExpensablesPendingApprovalWidgetDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    reject: (id: number, companyId: number, reason?: string) =>
      rejectGroup({ variables: { id, reason, companyId } }),
    loading,
  }
}

export const useRestartReview = () => {
  const [restartGroupReview, { loading }] = useMutation(RestartGroupReviewDocument, {
    extractorFn: (data) => data.expensesMutations.restartReviewExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
      ExpensesApprovedWidgetDocument,
      ReimbursementsPendingApprovalWidgetDocument,
      ReimbursementsApprovedWidgetDocument,
      ExpensablesPendingApprovalWidgetDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    restartReview: (id: number, companyId: number) =>
      restartGroupReview({ variables: { id, companyId } }),
    loading,
  }
}

export const useAddToPayroll = () => {
  const [addGroupToPayroll, { loading }] = useMutation(AddGroupToPayrollDocument, {
    extractorFn: (data) => data.expensesMutations.addToPayrollExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    addToPayroll: (id: number, period: PayrollPolicyPeriodTypesPeriodTypeEnum, companyId: number) =>
      addGroupToPayroll({ variables: { id, period, companyId } }),
    loading,
  }
}

export const usePayWithSepa = () => {
  const [sendToPayWithSepa, { loading }] = useMutation(PayGroupWithSepaDocument, {
    extractorFn: (data) => data.expensesMutations.sendToPayWithSepaExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    sendToPayWithSepa: (id: number, companyId: number) =>
      sendToPayWithSepa({ variables: { id, companyId } }),
    loading,
  }
}

export const useRemoveFromPayroll = () => {
  const [removeGroupFromPayroll, { loading }] = useMutation(RemoveGroupFromPayrollDocument, {
    extractorFn: (data) => data.expensesMutations.removeFromPayrollExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    removeFromPayroll: (id: number, companyId: number) =>
      removeGroupFromPayroll({ variables: { id, companyId } }),
    loading,
  }
}

export const useDelete = () => {
  const [deleteGroup, { loading }] = useMutation(DeleteGroupDocument, {
    extractorFn: (data) => data.expensesMutations.deleteExpensesExpensableGroup,
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
    onQueryUpdated: (query) => query.reobserve(),
  })

  return {
    delete: (id: number, companyId: number) => deleteGroup({ variables: { id, companyId } }),
    loading,
  }
}

export const useExportPdf = () => {
  const [exportGroupPdf, { loading }] = useMutation(ExportGroupPdfDocument, {
    extractorFn: (data) => data.expensesMutations.exportPdfExpensesExpensableGroup,
  })

  return {
    loading,
    exportPdf: (id: number, companyId: number) => exportGroupPdf({ variables: { id, companyId } }),
  }
}

export const useGroup = ({ id, employeeId, includeDrafts, includeAttachments, skip }: UseGroup) => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(GetGroupsDocument, {
    variables: {
      ids: [id],
      employeeIds: employeeId ? [employeeId] : null,
      includeDrafts,
      includeAttachments,
      companyId: company.id,
    },
    skip,
  })

  return {
    loading,
    group: data?.expenses?.expensableGroupsConnection?.nodes?.[0],
  }
}

export const useDraftGroups = () => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(GetGroupsDocument, {
    variables: {
      byStatus: 'draft',
      includeDrafts: true,
      first: 100,
      companyId: company.id,
    },
  })

  return {
    loading,
    groups: data?.expenses?.expensableGroupsConnection?.nodes ?? [],
  }
}
